import React from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Car, Clock, MapPin, Layers } from 'lucide-react';
import { formatPrice, formatNumber } from '@/lib/formatters';
import { powertrainLabels, bodyTypeLabels, getPowertrainBadgeVariant } from '@/types/database';
import type { ModelGroup } from '@/lib/cars/groupByModel';
import { buildCarImageUrl, buildCarImageSrcSet, CAR_CARD_SIZES } from '@/lib/car-image';

interface ModelCardProps {
  group: ModelGroup;
  /** Called when user clicks "Se X varianter" — switches list to variant mode filtered on this model */
  onShowVariants?: (modelId: string) => void;
}

export function ModelCard({ group, onShowVariants }: ModelCardProps) {
  const { brand, model, minPrice, maxPrice, minRange, maxRange, variantCount, powertrains, cheapestVariant, variants } = group;
  const upcomingCount = variants.filter(v => v.variant.availability_status === 'upcoming').length;
  const onSaleCount = variants.filter(v => v.variant.availability_status === 'on_sale').length;
  const allUpcoming = upcomingCount > 0 && onSaleCount === 0;
  const someUpcoming = upcomingCount > 0 && onSaleCount > 0;
  const [imageError, setImageError] = React.useState(false);
  const imageSource = { variant: cheapestVariant.variant, model };
  const imageSrc = buildCarImageUrl(imageSource, 640);
  const imageSrcSet = buildCarImageSrcSet(imageSource);

  const priceText = minPrice
    ? minPrice === maxPrice
      ? `${formatPrice(minPrice)}`
      : `${formatPrice(minPrice)} – ${formatPrice(maxPrice!)}`
    : null;

  const rangeText = minRange
    ? minRange === maxRange
      ? `${formatNumber(minRange)} km`
      : `${formatNumber(minRange)} – ${formatNumber(maxRange!)} km`
    : null;

  const detailHref = `/biler/${brand.slug}/${model.slug}/${cheapestVariant.variant.slug}`;

  return (
    <Link
      to={detailHref}
      className="block bg-card rounded-xl shadow-card overflow-hidden card-hover group relative"
    >
      {/* Image */}
      <div className="aspect-[16/10] bg-muted relative overflow-hidden">
        {imageSrc && !imageError ? (
          <img
            src={imageSrc}
            srcSet={imageSrcSet}
            sizes={CAR_CARD_SIZES}
            alt={`${brand.name} ${model.name}`}
            width={640}
            height={400}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted to-muted/50">
            <Car className="w-16 h-16 text-muted-foreground/60" />
          </div>
        )}

        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
          {allUpcoming && (
            <Badge variant="upcoming" className="gap-1">
              <Clock className="w-3 h-3" />
              Kommer snart
            </Badge>
          )}
          {someUpcoming && (
            <Badge variant="secondary" className="gap-1 shadow-md">
              <Clock className="w-3 h-3" />
              +{upcomingCount} kommer
            </Badge>
          )}
          {powertrains.map(p => (
            <Badge key={p} variant={getPowertrainBadgeVariant(p as any)}>
              {powertrainLabels[p as keyof typeof powertrainLabels] ?? p}
            </Badge>
          ))}
        </div>

        <div className="absolute top-3 right-3">
          <Badge variant="secondary" className="gap-1 shadow-md">
            <Layers className="w-3 h-3" />
            {variantCount} {variantCount === 1 ? 'variant' : 'varianter'}
          </Badge>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        <div>
          <p className="text-sm text-muted-foreground">{brand.name}</p>
          <h3 className="font-display font-semibold text-lg leading-tight">{model.name}</h3>
        </div>

        <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
          {model.body_type && (
            <span className="flex items-center gap-1">
              <Car className="w-3.5 h-3.5" />
              {bodyTypeLabels[model.body_type]}
            </span>
          )}
          {rangeText && (
            <span className="flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" />
              {rangeText}
            </span>
          )}
        </div>

        <div className="pt-2 border-t border-border flex items-end justify-between gap-3">
          <div>
            {priceText ? (
              <>
                <p className="text-xs text-muted-foreground">Fra</p>
                <p className="font-display font-bold text-xl text-primary leading-tight">
                  {priceText}
                </p>
              </>
            ) : (
              <p className="text-muted-foreground text-sm">Pris oplyses senere</p>
            )}
          </div>
          {variantCount > 1 && onShowVariants && (
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onShowVariants(group.modelId); }}
              className="text-xs font-medium text-primary hover:underline underline-offset-2"
            >
              Se varianter →
            </button>
          )}
        </div>
      </div>
    </Link>
  );
}
